import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { GenerateAccessTokensPayload } from '@/graphql/types/generateAccessTokensInput';
import { User } from '@/graphql/types/user';
import { getUserQuery, GetUserResponse } from '@/graphql/user/queries';
import {
  createProjectRequest,
  fetchAccessTokens,
  fetchSignPayload,
  getProjectsRequest,
  refreshAccessTokensRequest
} from '@/utils/api/auth';
import {
  clearTokenCookies,
  extractTokenData,
  getTokensFromCookies,
  handleAuthState,
  setTokenCookies
} from '@/utils/helpers/auth';
import { executeQueryWithJWT } from '@/utils/helpers/graphql';

interface AuthContextProps {
  isAuthenticated: boolean;
  isAuthReady: boolean;
  logout: () => void;
  login: (userPayload: GenerateAccessTokensPayload) => Promise<void>;
  tokenExpiresAt: Date | null;
  user: User | null;
  projectId: string | null;
  debug: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null);
  const [turboId, setTurboId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setTokenExpiresAt(null);
    setUser(null);

    if (typeof window !== 'undefined') {
      localStorage.clear();
    }

    clearTokenCookies();
    setIsAuthReady(true);
  }, []);

  const refreshToken = async () => {
    const savedTokens = getTokensFromCookies();

    if (!savedTokens?.refreshToken) {
      logout();
      return;
    }

    const newTokens = await refreshAccessTokensRequest(
      savedTokens.refreshToken
    );

    if (newTokens) {
      setTokenCookies(newTokens);
      const newTokenData = extractTokenData(newTokens.accessToken);

      if (newTokenData.expiresAt) {
        setTokenExpiresAt(newTokenData.expiresAt);
      }
    }
  };

  const planRefreshingToken = (): number => {
    const savedTokens = getTokensFromCookies();

    if (!savedTokens?.accessToken) return;

    const { expiresAt } = extractTokenData(savedTokens.accessToken);

    if (!expiresAt) return;

    const timeUntilExpiration = expiresAt.getTime() - Date.now() - 30000;

    const timeout = setTimeout(() => {
      refreshToken().catch(error => {
        console.error('Error refreshing token:', error);
        logout();
      });
    }, timeUntilExpiration);

    return timeout;
  };

  const fetchProjectData = async () => {
    try {
      const createdProjects = await getProjectsRequest();
      const projectId = createdProjects?.find(
        item => item.title === 'turbo-project'
      )?.id;
      const id: string = projectId || (await createProjectRequest()) || '';
      setTurboId(id);
      return id;
    } catch (error) {
      console.error('Error fetching projects:', error);
      return null;
    }
  };

  const getUser = async (projectId: string, userId: string) => {
    try {
      const userResponse = await executeQueryWithJWT<GetUserResponse>(
        getUserQuery,
        {
          input: {
            userId
          }
        }
      );

      if (userResponse?.data?.getUser) {
        setUser(userResponse.data.getUser);
        return userResponse.data.getUser;
      }

      return null;
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  };

  const getProjectId = async (): Promise<string> => {
    let projectId = turboId;

    if (!projectId) {
      projectId = await fetchProjectData();
    }

    if (!projectId) {
      throw new Error('Project ID is not available');
    }

    return projectId;
  };

  // will be changed after backend gem integration for turbo
  const authUser = async (
    userPayload: GenerateAccessTokensPayload,
    projectId: string
  ) => {
    // eslint-disable-next-line no-param-reassign
    userPayload.projectId = projectId;

    const payload = await fetchSignPayload(
      projectId,
      userPayload.userId,
      userPayload.name,
      userPayload.phones?.[0],
      userPayload.photo
    );

    if (!payload) {
      throw new Error('Failed to get signature');
    }

    const tokens = await fetchAccessTokens(
      payload.timestamp,
      payload.signature,
      projectId,
      userPayload.userId,
      userPayload.name,
      userPayload.phones?.[0],
      userPayload.photo
    );

    if (!tokens) {
      throw new Error('Failed to get tokens');
    }

    return tokens;
  };

  const handleTokensLoginData = async (
    userPayload: GenerateAccessTokensPayload,
    projectId: string
  ) => {
    const tokens = await authUser(userPayload, projectId);

    setTokenCookies(tokens);
    setIsAuthenticated(true);

    const { expiresAt } = extractTokenData(tokens.accessToken);

    if (expiresAt) {
      setTokenExpiresAt(expiresAt);
    }
  };

  const login = async (userPayload: GenerateAccessTokensPayload) => {
    try {
      setIsAuthReady(false);
      logout();
      const projectId = await getProjectId();

      await handleTokensLoginData(userPayload, projectId);

      // Will be changed during integration
      const realUserInfo = await getUser(projectId, userPayload.userId);

      if (!realUserInfo) {
        setUser({
          id: userPayload.userId,
          projectId,
          name: userPayload.name,
          photo: userPayload.photo,
          phones: userPayload.phones,
          chatEnabled: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      setIsAuthReady(true);
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthReady(true);
      throw error;
    }
  };

  const initAuth = async () => {
    try {
      // authentication process started
      setIsAuthReady(false);

      const projectId = await getProjectId();

      const savedTokens = getTokensFromCookies();

      if (!savedTokens) {
        logout();
        return;
      }

      const tokenData = extractTokenData(savedTokens.accessToken);

      // Verify token data is valid
      if (!tokenData.userId || !tokenData.expiresAt) {
        logout();
        return;
      }

      if (tokenData.expiresAt) {
        setTokenExpiresAt(tokenData.expiresAt);
      }

      // For reload we don't have saved user info
      getUser(projectId, tokenData.userId)
        .then(userData => {
          if (!userData) {
            logout();
          } else {
            setIsAuthReady(true);
            setIsAuthenticated(true);
          }
        })
        .catch(err => {
          console.error('Error fetching user data', err);
          logout();
        });
    } catch (err) {
      console.error('Authentication initialization error:', err);
      logout();
    }
  };

  // on page reload auth current user
  useEffect(() => {
    initAuth();
  }, []);

  useEffect(() => {
    const timeout = planRefreshingToken();

    return () => clearTimeout(timeout);
  }, [tokenExpiresAt]);

  useEffect(() => {
    handleAuthState({
      user,
      isAuthenticated,
      isAuthReady,
      setIsAuthenticated,
      setIsAuthReady
    });
  }, [user, isAuthenticated, isAuthReady]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthReady,
        logout,
        login,
        tokenExpiresAt,
        user,
        projectId: turboId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
